import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, PHONE_TEL, PHONE_DISPLAY, WHATSAPP_URL } from "../../lib/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled || open ? "border-b border-[#D4AF37]/15 bg-[#07040D]/80 py-3 backdrop-blur-xl" : "border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <Link to="/" aria-label="Astrologer & Spiritual Healers home" data-testid="nav-home-logo">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-9 lg:flex" aria-label="Main">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative text-sm tracking-wide transition-colors duration-300 ${isActive ? "text-[#F3E5AB]" : "text-slate-300 hover:text-white"}`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span className={`absolute -bottom-1.5 left-0 h-px bg-[#D4AF37] transition-[width] duration-500 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <a href={PHONE_TEL} data-testid="nav-call-button" aria-label={`Call ${PHONE_DISPLAY}`}
            className="hidden h-10 items-center gap-2 rounded-full border border-[#D4AF37]/30 px-4 text-sm text-slate-200 transition-colors duration-300 hover:border-[#D4AF37] hover:text-white md:flex">
            <Phone className="h-4 w-4 text-[#D4AF37]" /> {PHONE_DISPLAY}
          </a>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-button" aria-label="WhatsApp"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#07040D] md:hidden">
            <WhatsAppIcon className="h-4 w-4" />
          </a>
          <Link to="/book" data-testid="nav-book-button"
            className="hidden h-10 items-center rounded-full bg-[#D4AF37] px-5 text-sm font-medium text-[#07040D] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#F3E5AB] sm:flex">
            Book Appointment
          </Link>
          <button onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Toggle menu" aria-expanded={open}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#F3E5AB] lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 pb-6 pt-4 sm:px-8">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink to={n.to} end data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                    className={({ isActive }) => `font-display block border-b border-[#D4AF37]/10 py-3 text-3xl ${isActive ? "text-[#F3E5AB]" : "text-slate-200"}`}>
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-5 grid grid-cols-2 gap-3">
                <a href={PHONE_TEL} data-testid="mobile-nav-call" className="flex h-12 items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 text-sm text-slate-100">
                  <Phone className="h-4 w-4 text-[#D4AF37]" /> Call Now
                </a>
                <Link to="/book" data-testid="mobile-nav-book" className="flex h-12 items-center justify-center rounded-full bg-[#D4AF37] text-sm font-medium text-[#07040D]">
                  Book Now
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
